import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCuentas } from "../../../settings/hooks/useSettings";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { Label, Toast } from "../../../settings/components/SettingsUI";
import { overlayClose } from "../../../../shared/utils/overlayClose";
import fs from "../../shared/fichas.module.css";

const MONEDAS       = ["CRC","USD","EUR"];
const EMPTY_CUENTA  = {banco:"",numero_cuenta:"",iban:"",moneda:"CRC",saldo:"0"};
const MONEDA_SYMBOL = {CRC:"₡",USD:"$",EUR:"€"};
// Acento de cada moneda en las tarjetas (ver finance/shared/fichas.module.css).
const MONEDA_ACENTO = {CRC:"orange",USD:"green",EUR:"blue"};

/* ── estilos compartidos, theme-aware ─────────────────────────────────────── */
const inputSt = {
  background:"var(--bg-input, var(--bg))", border:"1px solid var(--border-input, var(--border-card))",
  borderRadius:8, padding:"10px 13px", color:"var(--text-primary)",
  fontSize:"0.87rem", outline:"none", width:"100%", boxSizing:"border-box",
  transition:"border-color 0.15s",
};

function Btn2({ children, onClick, variant="primary", disabled, small, type }) {
  const [h,setH]=useState(false);
  const cfg = {
    primary: { bg: h?"#ea6b10":"#f97316", color:"#fff", border:"#f97316" },
    ghost:   { bg: h?"var(--hover-bg)":"transparent", color:"var(--text-muted)", border:"var(--border-card)" },
    danger:  { bg: h?"#dc2626":"#ef4444", color:"#fff", border:"#ef4444" },
  }[variant];
  return (
    <button type={type||"button"} onClick={onClick} disabled={disabled}
      onMouseOver={()=>setH(true)} onMouseOut={()=>setH(false)}
      style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:9,
        color:cfg.color, padding:small?"7px 14px":"10px 20px",
        fontSize:small?"0.8rem":"0.86rem", fontWeight:400,
        cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.5:1,
        transition:"all 0.15s", display:"inline-flex", alignItems:"center",
        gap:8, whiteSpace:"nowrap" }}>
      {children}
    </button>
  );
}

/* ═══════════════════════ Modal shell (overlay + panel) ═══════════════════════ */
function ModalShell({ onClose, icon, iconColor="#f97316", title, subtitle, width=460, children, footer }) {
  return (
    <>
      <div {...overlayClose(onClose)} style={{
        position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.66)", backdropFilter:"blur(3px)",
        display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      }}>
        <div onClick={e=>e.stopPropagation()} style={{
          background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:16,
          width:`min(${width}px, 96vw)`, maxHeight:"90vh", overflow:"auto",
          boxShadow:"0 28px 70px rgba(0,0,0,0.5)",
        }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between",
            padding:"18px 22px", borderBottom:"1px solid var(--border-card)", gap:10 }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-start", minWidth:0 }}>
              <div style={{ width:40, height:40, borderRadius:11, flexShrink:0,
                background:`${iconColor}18`, border:`1px solid ${iconColor}30`,
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className={`fa-solid ${icon}`} style={{ color:iconColor, fontSize:"1rem" }}/>
              </div>
              <div style={{ minWidth:0 }}>
                <h2 style={{ margin:0, fontSize:"1.02rem", fontWeight:700, color:"var(--text-primary)" }}>{title}</h2>
                {subtitle && <p style={{ margin:"3px 0 0", color:"var(--text-muted)", fontSize:"0.79rem" }}>{subtitle}</p>}
              </div>
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--text-muted)",
              cursor:"pointer", fontSize:"1.05rem", padding:4, flexShrink:0 }}>
              <i className="fa-solid fa-xmark"/>
            </button>
          </div>
          <div style={{ padding:22 }}>{children}</div>
          {footer && (
            <div style={{ display:"flex", justifyContent:"flex-end", gap:10,
              padding:"14px 22px", borderTop:"1px solid var(--border-card)" }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function AlertBox({ children, onClose }) {
  return (
    <div style={{display:"flex",alignItems:"flex-start",gap:10,
      background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.28)",
      borderLeft:"3px solid #ef4444",borderRadius:8,padding:"10px 14px",marginBottom:14,
      color:"#f87171",fontSize:"0.83rem",lineHeight:1.55}}>
      <i className="fa-solid fa-triangle-exclamation" style={{color:"#f87171",flexShrink:0,marginTop:2}}/>
      <span style={{flex:1}}>{children}</span>
      {onClose && (
        <button type="button" onClick={onClose}
          style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",padding:0,flexShrink:0,fontSize:"0.85rem"}}>
          <i className="fa-solid fa-xmark"/>
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════ Modal: Nueva / Editar cuenta ═══════════════════════ */
function CuentaFormModal({ cuenta, onClose, onSave, multimoneda }) {
  const editando = !!cuenta;
  const [form,setForm] = useState(cuenta
    ? {banco:cuenta.banco, numero_cuenta:cuenta.numero_cuenta, iban:cuenta.iban||"", moneda:cuenta.moneda, saldo:cuenta.saldo}
    : EMPTY_CUENTA);
  const [usdBlock,setUsdBlock] = useState(false);
  const [saving,setSaving] = useState(false);
  const [err,setErr] = useState("");

  async function handleSave() {
    if (!form.banco.trim() || !form.numero_cuenta.trim()) { setErr("Completá banco y número de cuenta."); return; }
    if (!multimoneda && form.moneda!=='CRC') { setUsdBlock(true); return; }
    setUsdBlock(false); setErr(""); setSaving(true);
    try { await onSave(form); } catch(e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <ModalShell onClose={onClose} icon={editando?"fa-pencil":"fa-plus"} iconColor="#f97316"
      title={editando?"Editar cuenta bancaria":"Nueva cuenta bancaria"}
      subtitle={editando?cuenta.banco:"Registrá una cuenta para conciliación, traslados y pagos"}
      footer={<>
        <Btn2 variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn2>
        <Btn2 onClick={handleSave} disabled={saving}>
          {saving ? <><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</> : <><i className="fa-solid fa-floppy-disk"/>{editando?"Guardar cambios":"Crear cuenta"}</>}
        </Btn2>
      </>}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:4}}>
        <div style={{gridColumn:"span 2"}}><Label required>Banco</Label>
          <input style={inputSt} placeholder="Ej: Banco Nacional" value={form.banco}
            onChange={e=>setForm(f=>({...f,banco:e.target.value}))}/></div>
        <div><Label required>N° Cuenta</Label>
          <input style={inputSt} placeholder="Número de cuenta" value={form.numero_cuenta}
            onChange={e=>setForm(f=>({...f,numero_cuenta:e.target.value}))}/></div>
        <div><Label>Moneda</Label>
          <select style={inputSt} value={form.moneda} onChange={e=>{setForm(f=>({...f,moneda:e.target.value}));if(e.target.value==='CRC')setUsdBlock(false);}}>
            {MONEDAS.map(m=><option key={m}>{m}</option>)}</select></div>
        <div style={{gridColumn:"span 2"}}><Label>IBAN</Label>
          <input style={inputSt} placeholder="Ej: CR21015201001026284066" value={form.iban}
            maxLength={34} onChange={e=>setForm(f=>({...f,iban:e.target.value.toUpperCase()}))}/></div>
        <div style={{gridColumn:"span 2"}}><Label>{editando?"Saldo":"Saldo inicial"}</Label>
          <input style={inputSt} type="number" value={form.saldo}
            onChange={e=>setForm(f=>({...f,saldo:e.target.value}))}/></div>
      </div>
      {usdBlock && (
        <AlertBox onClose={()=>setUsdBlock(false)}>
          Para usar moneda <strong>{form.moneda}</strong>, primero debés activar{" "}
          <strong>Soporte Multimoneda (USD)</strong> en{" "}
          <Link to="/settings" style={{color:"#f87171",fontWeight:700}}>Configuración</Link>.
        </AlertBox>
      )}
      {err && <AlertBox>{err}</AlertBox>}
    </ModalShell>
  );
}

/* ═══════════════════════ Modal: Eliminar cuenta ═══════════════════════ */
function EliminarCuentaModal({ cuenta, onClose, onConfirm }) {
  const [deleting,setDeleting] = useState(false);
  const [err,setErr] = useState("");
  return (
    <ModalShell onClose={onClose} icon="fa-trash" iconColor="#f87171"
      title="Eliminar cuenta bancaria" subtitle={cuenta.banco}
      footer={<>
        <Btn2 variant="ghost" onClick={onClose} disabled={deleting}>Cancelar</Btn2>
        <Btn2 variant="danger" disabled={deleting} onClick={async()=>{
          setDeleting(true); setErr("");
          try { await onConfirm(); } catch(e) { setErr(e.message); setDeleting(false); }
        }}>
          {deleting ? <><i className="fa-solid fa-circle-notch fa-spin"/>Eliminando…</> : <><i className="fa-solid fa-trash"/>Sí, eliminar</>}
        </Btn2>
      </>}>
      <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
        <i className="fa-solid fa-triangle-exclamation" style={{color:"#f87171",fontSize:"1.3rem",marginTop:2,flexShrink:0}}/>
        <div style={{fontSize:"0.87rem",color:"var(--text-primary)",lineHeight:1.6}}>
          ¿Seguro que querés eliminar la cuenta <strong>"{cuenta.banco}"</strong> ({cuenta.numero_cuenta})?
          <br/>
          <span style={{color:"var(--text-muted)",fontSize:"0.81rem"}}>
            Esta acción no se puede deshacer. Si tiene movimientos o recibos vinculados, la cuenta puede estar bloqueada.
          </span>
        </div>
      </div>
      {err && <div style={{marginTop:14}}><AlertBox>{err}</AlertBox></div>}
    </ModalShell>
  );
}

/* ═══════════════════════ Modal: Saldo inicial de conciliación ═══════════════════════ */
function SaldoInicialModal({ cuenta, onClose, showToast }) {
  const [loading,setLoading] = useState(true);
  const [existing,setExisting] = useState(null);
  const [form,setForm] = useState({fecha:"",monto:""});
  const [saving,setSaving] = useState(false);
  const sym = MONEDA_SYMBOL[cuenta.moneda] ?? cuenta.moneda;

  useEffect(()=>{
    (async ()=>{
      setLoading(true);
      try {
        const res = await apiFetch(`/api/finance/saldo-inicial-conciliacion/?cuenta_bancaria=${cuenta.id}`);
        const json = await res.json();
        if (json?.id) { setExisting(json); setForm({fecha:json.fecha_corte||"", monto:String(json.saldo_banco)}); }
      } catch {} finally { setLoading(false); }
    })();
  }, [cuenta.id]);

  async function guardar() {
    setSaving(true);
    try {
      const body = {cuenta_bancaria:cuenta.id, fecha:form.fecha, monto:form.monto};
      const res = existing
        ? await apiFetch(`/api/finance/saldo-inicial-conciliacion/${existing.id}/`, {method:"PATCH", body:JSON.stringify(body)})
        : await apiFetch(`/api/finance/saldo-inicial-conciliacion/`, {method:"POST", body:JSON.stringify(body)});
      const json = await res.json();
      if (!res.ok) throw new Error(json.error||"Error al guardar saldo inicial");
      showToast(json.detail||"Guardado");
      onClose();
    } catch(e) { showToast(e.message,"error"); } finally { setSaving(false); }
  }
  async function eliminar() {
    if (!existing) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/finance/saldo-inicial-conciliacion/${existing.id}/`, {method:"DELETE"});
      const json = await res.json();
      if (!res.ok) throw new Error(json.error||"Error al eliminar");
      showToast(json.detail||"Eliminado");
      onClose();
    } catch(e) { showToast(e.message,"error"); } finally { setSaving(false); }
  }

  return (
    <ModalShell onClose={onClose} icon="fa-scale-balanced" iconColor="#60a5fa"
      title="Saldo inicial de conciliación" subtitle={cuenta.banco}
      footer={loading ? null : <>
        <Btn2 variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn2>
        {existing && <Btn2 variant="danger" disabled={saving} onClick={eliminar}><i className="fa-solid fa-trash"/>Eliminar</Btn2>}
        <Btn2 disabled={saving} onClick={guardar}>
          {saving ? <><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</> : <><i className="fa-solid fa-floppy-disk"/>Guardar</>}
        </Btn2>
      </>}>
      {loading ? (
        <div style={{color:"var(--text-muted)",fontSize:"0.85rem",textAlign:"center",padding:20}}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{marginRight:8}}/>Cargando…
        </div>
      ) : (
        <>
          <p style={{margin:"0 0 16px",color:"var(--text-muted)",fontSize:"0.8rem",lineHeight:1.6}}>
            Saldo ya conciliado con el banco al migrar esta cuenta al sistema. Las conciliaciones
            posteriores parten de este monto en lugar de {sym}0.00.
          </p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div><Label required>Fecha</Label>
              <input style={inputSt} type="date" value={form.fecha} onChange={e=>setForm(f=>({...f,fecha:e.target.value}))}/></div>
            <div><Label required>Monto ({sym})</Label>
              <input style={inputSt} type="number" value={form.monto} onChange={e=>setForm(f=>({...f,monto:e.target.value}))}/></div>
          </div>
        </>
      )}
    </ModalShell>
  );
}

/* ═══════════════════════ Tarjeta bancaria (vista de tarjetas) ═══════════════════════ */
function BankCard({ cuenta, onEdit, onDelete, onConciliar, onSaldoInicial }) {
  const sym = MONEDA_SYMBOL[cuenta.moneda] ?? cuenta.moneda;
  return (
    <div className={`${fs.card} ${fs.efecto} ${fs[MONEDA_ACENTO[cuenta.moneda] ?? "orange"]}`}>
      <i className={`fa-solid fa-building-columns ${fs.decoIcon}`}/>
      <div className={fs.cardHead}>
        <div className={fs.cardIcon}><i className="fa-solid fa-building-columns"/></div>
        <div className={fs.cardTitleBox}>
          <div className={fs.cardTitle}><span>{cuenta.banco}</span></div>
          <div style={{marginTop:6}}><span className={fs.badge}>{cuenta.moneda}</span></div>
        </div>
        <span className={`${fs.badge} ${fs.green}`}>Activa</span>
      </div>

      <div className={fs.divider}/>
      <div className={fs.rows}>
        <div className={fs.row}>
          <i className="fa-solid fa-hashtag"/><span>Número de cuenta</span>
          <span className={fs.mono}>{cuenta.numero_cuenta}</span>
        </div>
        <div className={fs.row}>
          <i className="fa-solid fa-landmark"/><span>IBAN</span>
          <span className={fs.mono} title={cuenta.iban || ""}>{cuenta.iban || "-"}</span>
        </div>
      </div>
      <div className={fs.dashed}/>

      <div className={fs.saldoBox}>
        <div style={{minWidth:0}}>
          <div className={fs.saldoLabel}>Saldo actual</div>
          <div className={fs.saldoValue}>{sym}{formatCurrency(Number(cuenta.saldo))}</div>
        </div>
        <div className={fs.symbol}>{sym}</div>
      </div>

      <div className={fs.actions}>
        <button type="button" className={fs.action} onClick={onConciliar}>
          <i className="fa-solid fa-code-compare"/>Conciliar
        </button>
        <button type="button" className={fs.action} onClick={onSaldoInicial} title="Saldo inicial de conciliación">
          <i className="fa-solid fa-scale-balanced"/>Saldo inicial
        </button>
        <button type="button" className={fs.action} onClick={onEdit}>
          <i className="fa-solid fa-pencil"/>Editar
        </button>
        <button type="button" className={`${fs.action} ${fs.danger}`} onClick={onDelete}>
          <i className="fa-solid fa-trash"/>Eliminar
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════ Fila bancaria (vista lista) ═══════════════════════ */
function BankRow({ cuenta, onEdit, onDelete, onConciliar, onSaldoInicial }) {
  const sym = MONEDA_SYMBOL[cuenta.moneda] ?? cuenta.moneda;
  return (
    <div className={`${fs.listRow} ${fs[MONEDA_ACENTO[cuenta.moneda] ?? "orange"]}`}>
      <div className={fs.cardIcon}><i className="fa-solid fa-building-columns"/></div>
      <div className={fs.listMain}>
        <strong>{cuenta.banco}</strong>
        <small><i className="fa-solid fa-hashtag" style={{fontSize:"0.6rem",marginRight:4}}/>{cuenta.numero_cuenta}</small>
      </div>
      <span className={fs.badge}>{cuenta.moneda}</span>
      <span className={`${fs.badge} ${fs.green}`}>Activa</span>
      <div className={fs.listAmount}>{sym}{formatCurrency(Number(cuenta.saldo))}</div>
      <div className={fs.iconBtns}>
        <button type="button" className={fs.iconBtn} title="Conciliar" onClick={onConciliar}><i className="fa-solid fa-code-compare"/></button>
        <button type="button" className={fs.iconBtn} title="Saldo inicial de conciliación" onClick={onSaldoInicial}><i className="fa-solid fa-scale-balanced"/></button>
        <button type="button" className={fs.iconBtn} title="Editar" onClick={onEdit}><i className="fa-solid fa-pencil"/></button>
        <button type="button" className={`${fs.iconBtn} ${fs.danger}`} title="Eliminar" onClick={onDelete}><i className="fa-solid fa-trash"/></button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════ Página principal ═══════════════════════════════ */
export default function CuentasBancariasPage() {
  const { cuentas, loading, create, remove, update } = useCuentas();
  const multimoneda = useMultimoneda();
  const navigate = useNavigate();
  const [sinRelBancos, setSinRelBancos] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid"); // grid | list
  useEffect(()=>{
    apiFetch("/api/core/relaciones-catalogo/").then(r=>r.json()).then(d=>{
      setSinRelBancos(!d.relaciones?.rel_cuenta_bancos?.cuenta_actual);
    }).catch(()=>{});
  },[]);

  const [toast,setToast]=useState({msg:"",type:""});
  const showToast=(msg,type="success")=>{setToast({msg,type});setTimeout(()=>setToast({msg:"",type:""}),3500);};

  // Modales
  const [modalNueva, setModalNueva]           = useState(false);
  const [modalEditar, setModalEditar]         = useState(null); // cuenta | null
  const [modalEliminar, setModalEliminar]     = useState(null); // cuenta | null
  const [modalSaldoIni, setModalSaldoIni]     = useState(null); // cuenta | null

  const totalesPorMoneda = MONEDAS
    .map(m => ({ moneda:m, total: cuentas.filter(c=>c.moneda===m).reduce((s,c)=>s+Number(c.saldo||0),0), n: cuentas.filter(c=>c.moneda===m).length }))
    .filter(t => t.n > 0);

  const q = search.trim().toLowerCase();
  const cuentasFiltradas = !q ? cuentas : cuentas.filter(c =>
    (c.banco||"").toLowerCase().includes(q) ||
    (c.numero_cuenta||"").toLowerCase().includes(q) ||
    (c.iban||"").toLowerCase().includes(q)
  );

  const goConciliar = (cuenta) => navigate(`/finanzas/conciliacion?cuenta=${cuenta.id}`);
  function nuevaCuenta() {
    if (sinRelBancos) { showToast("Configurá primero la relación de Bancos en Relaciones Contables.","error"); return; }
    setModalNueva(true);
  }
  const acciones = (c) => ({
    onEdit: ()=>setModalEditar(c),
    onDelete: ()=>setModalEliminar(c),
    onConciliar: ()=>goConciliar(c),
    onSaldoInicial: ()=>setModalSaldoIni(c),
  });

  return (
    <div className={fs.page}>
      <span style={{color:"var(--text-muted)"}}>
        <Link to="/"><span style={{color:"var(--text-muted)"}}>Inicio </span></Link>/ <span style={{color:"var(--text-primary)"}}>Cuentas Bancarias</span>
      </span>
      <div className={fs.header}>
        <div>
          <h2 className="p-0 title">Cuentas Bancarias</h2>
          <p>Cuentas bancarias registradas para conciliación, traslados y pagos.</p>
        </div>
        <button type="button" className={fs.btnPrimary} onClick={nuevaCuenta}>
          <i className="fa-solid fa-plus"/>Nueva cuenta
        </button>
      </div>

      <Toast msg={toast.msg} type={toast.type}/>

      {sinRelBancos && (
        <div style={{display:"flex",alignItems:"flex-start",gap:10,
          background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",
          borderRadius:10,padding:"12px 16px",marginBottom:16}}>
          <i className="fa-solid fa-triangle-exclamation" style={{color:"#fbbf24",marginTop:2,flexShrink:0}}/>
          <div>
            <p style={{margin:"0 0 3px",color:"#fbbf24",fontWeight:700,fontSize:"0.82rem"}}>
              Relación de Bancos sin configurar
            </p>
            <p style={{margin:0,color:"var(--text-muted)",fontSize:"0.76rem"}}>
              No podrás registrar cuentas bancarias hasta asignar la cuenta padre de{" "}
              <strong style={{color:"#fbbf24"}}>Bancos</strong> en{" "}
              <Link to="/settings?tab=relaciones" style={{color:"#f97316",fontWeight:700}}>Relaciones Contables</Link>.
            </p>
          </div>
        </div>
      )}

      {/* ── Saldo total por moneda ─────────────────────────────────────── */}
      {totalesPorMoneda.length > 0 && (
        <div className={fs.kpiGrid} style={{gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))"}}>
          {totalesPorMoneda.map(({moneda,total,n})=>(
            <div key={moneda} className={`${fs.kpi} ${fs[MONEDA_ACENTO[moneda]]}`}>
              <div className={fs.kpiIcon}><i className="fa-solid fa-building-columns"/></div>
              <div className={fs.kpiBody}>
                <p className={fs.kpiLabel}>Saldo total {moneda}</p>
                <p className={fs.kpiValue}>{MONEDA_SYMBOL[moneda]}{formatCurrency(total)}</p>
                <p className={fs.kpiSub}><i className="fa-solid fa-coins"/>{n} {n===1?"cuenta":"cuentas"}</p>
              </div>
              <i className={`fa-solid fa-building-columns ${fs.kpiDeco}`}/>
            </div>
          ))}
        </div>
      )}

      {/* ── Mis cuentas ─────────────────────────────────────────────────── */}
      <div className={fs.panel}>
        <div className={fs.panelHead}>
          <div>
            <h3 className={fs.panelTitle}>Mis cuentas</h3>
            <p className={fs.panelSub}>Gestioná tus cuentas bancarias registradas.</p>
          </div>
          {!loading && cuentas.length > 0 && (
            <div className={fs.tools}>
              <div className={fs.search}>
                <i className="fa-solid fa-magnifying-glass"/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar cuenta…"/>
              </div>
              <div className={fs.toggle}>
                <button type="button" title="Vista de tarjetas" className={view==="grid"?fs.on:""} onClick={()=>setView("grid")}>
                  <i className="fa-solid fa-table-cells-large"/>
                </button>
                <button type="button" title="Vista de lista" className={view==="list"?fs.on:""} onClick={()=>setView("list")}>
                  <i className="fa-solid fa-list"/>
                </button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className={fs.empty}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{opacity:1,color:"var(--brand)",fontSize:"1.4rem"}}/>Cargando…
          </div>
        ) : cuentas.length===0 ? (
          <div className={fs.empty}>
            <i className="fa-solid fa-building-columns"/>
            <p style={{margin:"0 0 14px"}}>No hay cuentas registradas.</p>
            <button type="button" className={fs.btnPrimary} onClick={nuevaCuenta}><i className="fa-solid fa-plus"/>Registrar la primera</button>
          </div>
        ) : cuentasFiltradas.length===0 ? (
          <div className={fs.empty}>
            <i className="fa-solid fa-magnifying-glass"/>
            Ninguna cuenta coincide con "{search}".
          </div>
        ) : view === "grid" ? (
          <div className={fs.cardGrid} style={{"--card-min":"400px"}}>
            {cuentasFiltradas.map(c=> <BankCard key={c.id} cuenta={c} {...acciones(c)}/>)}
          </div>
        ) : (
          <div className={fs.list}>
            {cuentasFiltradas.map(c=> <BankRow key={c.id} cuenta={c} {...acciones(c)}/>)}
          </div>
        )}
      </div>

      {/* ── Franja resumen: cuentas y totales por moneda ────────────────── */}
      {!loading && totalesPorMoneda.length > 0 && (
        <div className={fs.strip}>
          {totalesPorMoneda.flatMap(({moneda,total,n}) => {
            const acento = fs[MONEDA_ACENTO[moneda]];
            return [
              <div key={`n-${moneda}`} className={`${fs.stripItem} ${acento}`}>
                <div className={fs.stripIcon}><i className="fa-solid fa-building-columns"/></div>
                <div>
                  <div className={fs.stripValue}>{n}</div>
                  <div className={fs.stripLabel}>{n===1?"Cuenta":"Cuentas"} en {moneda}</div>
                </div>
              </div>,
              <div key={`t-${moneda}`} className={`${fs.stripItem} ${acento}`}>
                <div className={fs.stripIcon}><i className={`fa-solid ${moneda==="USD"?"fa-dollar-sign":"fa-coins"}`}/></div>
                <div>
                  <div className={fs.stripValue}>{MONEDA_SYMBOL[moneda]}{formatCurrency(total)}</div>
                  <div className={fs.stripLabel}>Total en {moneda}</div>
                </div>
              </div>,
            ];
          })}
        </div>
      )}

      {/* ── Modales ──────────────────────────────────────────────────────── */}
      {modalNueva && (
        <CuentaFormModal multimoneda={multimoneda}
          onClose={()=>setModalNueva(false)}
          onSave={async(form)=>{
            await create(form);
            setModalNueva(false);
            showToast("Cuenta creada");
          }}
        />
      )}
      {modalEditar && (
        <CuentaFormModal cuenta={modalEditar} multimoneda={multimoneda}
          onClose={()=>setModalEditar(null)}
          onSave={async(form)=>{
            await update(modalEditar.id, form);
            setModalEditar(null);
            showToast("Cuenta actualizada");
          }}
        />
      )}
      {modalEliminar && (
        <EliminarCuentaModal cuenta={modalEliminar}
          onClose={()=>setModalEliminar(null)}
          onConfirm={async()=>{
            await remove(modalEliminar.id);
            setModalEliminar(null);
            showToast("Cuenta eliminada");
          }}
        />
      )}
      {modalSaldoIni && (
        <SaldoInicialModal cuenta={modalSaldoIni}
          onClose={()=>setModalSaldoIni(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
}
